import type {
  BrandOfferResponse,
  OffersListPagination,
  OffersListResult,
} from '@/types/BrandOffer.types';

function num(v: unknown, fallback: number): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function bool(v: unknown): boolean {
  return Boolean(v);
}

function emptyPagination(
  requestedPage: number,
  itemsPerPage: number
): OffersListPagination {
  return {
    currentPage: requestedPage,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage,
    hasNext: false,
    hasPrevious: false,
  };
}

/**
 * Supports `{ offers, pagination }` (preferred) and legacy `{ results, count }`.
 */
export function parseOffersListResponse(
  data: unknown,
  ctx: { requestedPage: number; itemsPerPage: number }
): OffersListResult {
  const { requestedPage, itemsPerPage } = ctx;
  if (!data || typeof data !== 'object') {
    return {
      offers: [],
      pagination: emptyPagination(requestedPage, itemsPerPage),
    };
  }
  const d = data as Record<string, unknown>;
  const offersRaw = d.offers ?? d.results;
  const list = Array.isArray(offersRaw) ? offersRaw : [];
  const paginationRaw = d.pagination as Record<string, unknown> | undefined;

  if (paginationRaw && typeof paginationRaw === 'object') {
    const perPage = num(
      paginationRaw.items_per_page ?? paginationRaw.itemsPerPage,
      itemsPerPage
    );
    const totalItems = num(
      paginationRaw.total_items ?? paginationRaw.totalItems,
      list.length
    );
    const totalPagesFromApi = num(
      paginationRaw.total_pages ?? paginationRaw.totalPages,
      0
    );
    const totalPages =
      totalPagesFromApi > 0
        ? totalPagesFromApi
        : totalItems > 0 && perPage > 0
          ? Math.max(1, Math.ceil(totalItems / perPage))
          : 1;

    return {
      offers: list as BrandOfferResponse[],
      pagination: {
        currentPage: num(
          paginationRaw.current_page ?? paginationRaw.currentPage,
          requestedPage
        ),
        totalPages: Math.max(1, totalPages),
        totalItems,
        itemsPerPage: perPage,
        hasNext: bool(paginationRaw.has_next ?? paginationRaw.hasNext),
        hasPrevious: bool(
          paginationRaw.has_previous ?? paginationRaw.hasPrevious
        ),
      },
    };
  }

  const count = num(d.count, list.length);
  const totalPages =
    count > 0 ? Math.max(1, Math.ceil(count / itemsPerPage)) : 1;
  const hasNext = requestedPage < totalPages;
  const hasPrevious = requestedPage > 1;

  return {
    offers: list as BrandOfferResponse[],
    pagination: {
      currentPage: requestedPage,
      totalPages,
      totalItems: count,
      itemsPerPage,
      hasNext,
      hasPrevious,
    },
  };
}
